import { Button } from 'components/Button';
import { Form, FormField, SelectOption, TextInput } from 'components/Forms';
import { CheckboxInput } from 'components/Forms/inputs/CheckboxInput';
import { Heading } from 'components/Typography';
import { PhysicianForm } from 'domains/signup/data/types';
import { InsuranceContextV4, withInsuranceMachineStateV4 } from 'domains/signupv4/Insurance/context';

import { useCallback } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { isPhoneNumberValid } from 'utils/phoneNumber';
import * as yup from 'yup';

type Props = {
  defaultValues?: PhysicianForm;
  onSubmit: (value: PhysicianForm) => void;
  primaryPhysicianOptions?: SelectOption[];
};

const getFormSchema = (t: TFunction) =>
  yup.object().shape({
    noPrimaryPhysician: yup.boolean().required(),
    name: yup
      .string()
      .trim()
      .when('noPrimaryPhysician', {
        is: true,
        then: schema => schema.optional(),
        otherwise: schema => schema.required(t('errors.required')),
      }),
    office: yup
      .string()
      .trim()
      .when('noPrimaryPhysician', {
        is: true,
        then: schema => schema.optional(),
        otherwise: schema => schema.required(t('errors.required')),
      }),
    phoneNumber: yup
      .string()
      .trim()
      .nullable()
      .test('phone', t('errors.invalidPhone'), value => {
        return !value || value === '' || isPhoneNumberValid(value);
      }),
    website: yup.string().trim().optional(),
  });

const EnterPrimaryPhysician: React.FC<Props> = ({ defaultValues, onSubmit }) => {
  const { t } = useTranslation();
  return (
    <Form
      onSubmit={onSubmit}
      schema={getFormSchema(t)}
      defaultValues={defaultValues}
      validationMode="all"
      flashErrors={false}
    >
      <Heading level="h2" className="mb-16">
        {t('signup.direct.steps.enterPrimaryPhysician.title')}
      </Heading>
      <Heading level="h7" className="mb-64 sm-d:mb-40">
        {t('signup.direct.steps.enterPrimaryPhysician.subtitle')}
      </Heading>
      <PrimaryPhysicianForm />
    </Form>
  );
};

const PrimaryPhysicianForm = () => {
  const { t } = useTranslation();
  const { isValid } = useFormState();
  const noPrimaryPhysician = useWatch({
    name: 'noPrimaryPhysician',
  });

  return (
    <>
      <div className="mb-40 sm-d:mb-40">
        <FormField
          component={CheckboxInput}
          name="noPrimaryPhysician"
          id="noPrimaryPhysician"
          size="md"
          option={{
            label: t(`signup.direct.steps.enterPrimaryPhysician.noPrimaryPhysician.label`),
            value: 'true',
            startAdornment: true,
          }}
        />
      </div>

      {!noPrimaryPhysician && (
        <>
          <FormField
            component={TextInput}
            name="name"
            label={t('signup.direct.steps.enterPrimaryPhysician.name.label') as string}
            className="mb-40 sm-d:mb-40"
          />
          <FormField
            component={TextInput}
            name="office"
            label={t('signup.direct.steps.enterPrimaryPhysician.office.label') as string}
            className="mb-40 sm-d:mb-40"
          />
          <FormField
            component={TextInput}
            name="phoneNumber"
            label={t('signup.direct.steps.enterPrimaryPhysician.phoneNumber.label') as string}
            className="mb-40 sm-d:mb-40"
          />
          <FormField
            component={TextInput}
            name="website"
            label={t('signup.direct.steps.enterPrimaryPhysician.website.label') as string}
            className="mb-40 sm-d:mb-40"
          />
        </>
      )}

      <Button
        data-testid="primary-physician-submit-button"
        type="submit"
        size="lg"
        className="mt-64 sm-d:mt-40 sm-d:w-full"
        disabled={!isValid}
      >
        {t('signup.direct.steps.enterPrimaryPhysician.submit')}
      </Button>
    </>
  );
};
const InsuranceWrapper = () => {
  const primaryPhysician = InsuranceContextV4.useSelector(({ context }) => context.data.primaryPhysician);
  const { send } = InsuranceContextV4.useActorRef();

  const onSubmit = useCallback(
    (values: PhysicianForm) => {
      send({
        type: 'submitPrimaryPhysician',
        primaryPhysician: values,
      });
    },
    [send],
  );

  return <EnterPrimaryPhysician defaultValues={primaryPhysician} onSubmit={onSubmit} />;
};

export const InsuranceV4EnterPrimaryPhysician = withInsuranceMachineStateV4(InsuranceWrapper, 'enterPrimaryPhysician');
